import { useNavigate, useLocation } from "react-router-dom";

import {
  Box,
  Fab,
  Fade,
  Typography,
  Grid,
  AppBar,
  Toolbar,
  CssBaseline,
  useScrollTrigger,
  Breadcrumbs,
  Button,
} from "@mui/material";
//icon
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

// Import Type
import { LayoutProps } from "./data/AppLayout.type";
import { useBreadcrumb } from "context/breadcrumb.context";
import { useExportPack } from "context/exportPack.context";
import { useAppState } from "context/appState.context";
// Logo
import nzdmlogo from "assets/Images/NZC_ident_black.png";
import LoggedInPersonName from "./header/logged-in-person-name.component";
import LoggedInAvatarMenu from "./header/logged-in-avatar-menu.component";

/**
 * ScrollTop component to handle scrolling back to the top when a trigger is activated.
 *
 * @param {LayoutProps} props - The properties for the ScrollTop component.
 * @returns {JSX.Element} The rendered ScrollTop component.
 */
function ScrollTop(props: Readonly<LayoutProps>): JSX.Element {
  const { children, window } = props;
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  // Use the scroll trigger to check if the user has scrolled more than 100px
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (
      (event.target as HTMLDivElement).ownerDocument || document
    ).querySelector("#back-to-top-anchor");

    if (anchor) {
      anchor.scrollIntoView({
        block: "center",
      });
    }
  };

  return (
    <Fade in={trigger}>
      <Box
        onClick={handleClick}
        aria-label="Scroll back to top"
        sx={{ position: "fixed", bottom: 16, right: 16 }}
      >
        {children}
      </Box>
    </Fade>
  );
}

/**
 * AppLayout component to define the main layout structure for the application.
 *
 * @param {LayoutProps} props - The properties for the AppLayout component.
 * @returns {JSX.Element} The rendered AppLayout component.
 */
export default function AppLayout(props: Readonly<LayoutProps>): JSX.Element {
  const Navigate = useNavigate();
  const location = useLocation();
  const { breadcrumbs } = useBreadcrumb();
  const { openDrawer, setExportData } = useExportPack();
  const { locationName: appLocationName } = useAppState();
  const locationIdMatch = /^\/Location\/(\d+)/i.exec(location.pathname);
  const isLocationLevel = !!locationIdMatch;
  const currentLocationId = locationIdMatch ? Number(locationIdMatch[1]) : 0;
  return (
    <>
      <CssBaseline />
      <AppBar>
        <Toolbar>
          <Grid container spacing={2}>
            <Grid item xs={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <img
                src={nzdmlogo}
                alt={"arcadis logo"}
                height={30}
                style={{ marginTop: "10px" }}
              />

            </Grid>
            <Grid item xs={6}  sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {/* Breadcrumbs - visible only when items exist */}
              {breadcrumbs.length > 0 && (
                <Breadcrumbs
                  aria-label="breadcrumb"
                  sx={{ fontSize: "0.875rem", ml: 1 }}
                  data-testid="app-breadcrumbs"
                >
                  <HomeOutlinedIcon
                    sx={{
                      color: "primary.main",
                      cursor: "pointer",
                      fontSize: "1.25rem",
                      "&:hover": { opacity: 0.7 },
                    }}
                    onClick={() => Navigate("/Locations", { state: { viewLevel: "client" } })}
                    data-testid="home-button"
                  />
                  {breadcrumbs.map((crumb) => (
                    <Typography
                      key={crumb.label}
                      sx={{
                        color: crumb.isActive ? "text.primary" : "primary.main",
                        cursor: crumb.isActive ? "default" : "pointer",
                        fontSize: "0.875rem",
                        fontWeight: crumb.isActive ? 600 : 400,
                        "&:hover": !crumb.isActive
                          ? { textDecoration: "underline" }
                          : {},
                      }}
                      onClick={() => {
                        if (!crumb.isActive && crumb.onClick) {
                          crumb.onClick();
                        }
                      }}
                    >
                      {crumb.label}
                    </Typography>
                  ))}
                </Breadcrumbs>
              )}
              </Grid>
            <Grid item sx={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 1 }}>
              {isLocationLevel && props.isAuthorized && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={() => {
                    setExportData({
                      locationId: currentLocationId,
                      locationName: appLocationName,
                      dashboardSection: '',
                      ghgScope: [],
                      measureCount: 0,
                    });
                    openDrawer();
                  }}
                  data-testid="export-pack-button"
                  sx={{
                    marginRight: "1rem",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: "#E87722",
                    color: "#E87722",
                    borderRadius: "8px",
                    px: 2,
                    "&:hover": {
                      borderColor: "#D06A1E",
                      bgcolor: "#FFF7F0",
                    },
                  }}
                >
                  Export Pack
                </Button>
              )}
              <Box
                display="flex"
                justifyContent="flex-end"
                sx={{ display: { xs: "none", sm: "flex" } }}
              >
                {props.isAuthorized ? (
                  <LoggedInPersonName
                    variant="body1"
                    color="black"
                    fontWeight={300}
                    account={props.account}
                  />
                ) : null}
              </Box>
              {props.isAuthorized ? (
                <LoggedInAvatarMenu
                  account={props.account}
                  signOut={props.signOut}
                ></LoggedInAvatarMenu>
              ) : null}
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <Toolbar id="back-to-top-anchor" />

      <Box sx={{ m: 2 ,mr:0}}>
        <Grid container>{props.children}</Grid>
      </Box>
      <ScrollTop {...props}>
        <Fab size="small" aria-label="scroll back to top">
          <KeyboardArrowUpIcon />
        </Fab>
      </ScrollTop>
    </>
  );
}
